import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, AlertCircle, Search, Sparkles, Star, Users, Clock, Download } from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { isUserAdmin } from '../../lib/auth';
import { supabase } from '../../lib/supabase';
import { LoadingSpinner } from '../../components/LoadingSpinner';

interface TrainingTestimonial {
  id: string;
  participant_name: string;
  email: string | null;
  training_type: string;
  testimonial_text: string;
  consent_given: boolean;
  review_status: 'new' | 'reviewed' | 'featured' | 'archived';
  admin_note: string | null;
  submitted_at: string;
  reviewed_at: string | null;
}

const TRAINING_LABELS: Record<string, string> = {
  '7-day-new-model-nlp': '7-Day New Model of NLP',
  'nlp-os': 'NLP OS',
  'classical-nlp-practitioner': 'Classical NLP Practitioner',
  'new-model-enrolment-method': 'New Model of NLP Enrolment Method Training',
};

const STATUS_LABELS: Record<TrainingTestimonial['review_status'], string> = {
  new: 'New',
  reviewed: 'Reviewed',
  featured: 'Featured',
  archived: 'Archived',
};

export function TestimonialReviews() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [testimonials, setTestimonials] = useState<TrainingTestimonial[]>([]);
  const [selectedTestimonial, setSelectedTestimonial] = useState<TrainingTestimonial | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [trainingFilter, setTrainingFilter] = useState('all');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadTestimonials();
  }, [user, navigate]);

  const loadTestimonials = async () => {
    if (!user?.id) return;
    const isAdmin = await isUserAdmin(user.id);
    if (!isAdmin) {
      navigate('/login');
      return;
    }

    const { data, error: loadError } = await supabase
      .from('training_testimonials')
      .select('*')
      .order('submitted_at', { ascending: false });

    if (loadError) {
      setError('We could not load the testimonials right now.');
    } else {
      setTestimonials((data || []) as TrainingTestimonial[]);
    }
    setIsLoading(false);
  };

  const filteredTestimonials = useMemo(() => testimonials.filter((t) => {
    const matchesSearch = `${t.participant_name} ${t.testimonial_text}`
      .toLowerCase()
      .includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || t.review_status === statusFilter;
    const matchesTraining = trainingFilter === 'all' || t.training_type === trainingFilter;
    return matchesSearch && matchesStatus && matchesTraining;
  }), [testimonials, search, statusFilter, trainingFilter]);

  const counts = {
    total: testimonials.length,
    new: testimonials.filter((t) => t.review_status === 'new').length,
    reviewed: testimonials.filter((t) => t.review_status === 'reviewed').length,
    featured: testimonials.filter((t) => t.review_status === 'featured').length,
  };

  const updateTestimonial = async (updates: Partial<TrainingTestimonial>) => {
    if (!selectedTestimonial) return;
    setIsSaving(true);
    setError(null);

    const { data, error: updateError } = await supabase
      .from('training_testimonials')
      .update({ ...updates, reviewed_at: new Date().toISOString() })
      .eq('id', selectedTestimonial.id)
      .select()
      .maybeSingle();

    if (updateError || !data) {
      setError('We could not save those review changes.');
    } else {
      const updated = data as TrainingTestimonial;
      setTestimonials((current) => current.map((t) => t.id === updated.id ? updated : t));
      setSelectedTestimonial(updated);
    }
    setIsSaving(false);
  };

  const downloadCSV = () => {
    const rows = [['Name', 'Email', 'Training', 'Testimonial', 'Consent Given', 'Status', 'Submitted At']];
    filteredTestimonials.forEach((t) => rows.push([
      t.participant_name,
      t.email || '',
      TRAINING_LABELS[t.training_type] || t.training_type,
      t.testimonial_text,
      t.consent_given ? 'Yes' : 'No',
      STATUS_LABELS[t.review_status],
      new Date(t.submitted_at).toLocaleString(),
    ]));
    const csv = rows.map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'testimonials.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const downloadNamesAndTestimonials = () => {
    const rows = [['Name', 'Testimonial']];
    filteredTestimonials.forEach((t) => rows.push([
      t.participant_name,
      t.testimonial_text,
    ]));
    const csv = rows.map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'testimonials-names-and-text.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) return <LoadingSpinner message="Loading testimonials..." />;

  return (
    <div className="min-h-screen bg-nlp-cream" style={{ fontFamily: 'Urbanist, sans-serif' }}>
      <header className="bg-white/85 backdrop-blur-sm border-b border-nlp-cream-dark shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="p-2 hover:bg-nlp-cream rounded-lg transition-colors text-nlp-taupe">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="p-2.5 bg-nlp-forest rounded-xl shadow-soft">
              <Sparkles className="w-6 h-6 text-nlp-gold-light" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-nlp-forest">Testimonials</h1>
              <p className="text-sm text-nlp-taupe">Student testimonials from NLP training</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={downloadNamesAndTestimonials}
              className="flex items-center gap-2 px-4 py-2.5 bg-nlp-olive hover:bg-nlp-olive-light text-white text-sm font-medium rounded-xl transition-colors"
            >
              <Download className="w-4 h-4" />
              Export Names & Testimonials
            </button>
            <button
              onClick={downloadCSV}
              className="flex items-center gap-2 px-4 py-2.5 bg-nlp-forest hover:bg-nlp-forest-light text-white text-sm font-medium rounded-xl transition-colors"
            >
              <Download className="w-4 h-4" />
              Export Full CSV
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {error && <div className="mb-6 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Testimonials', value: counts.total, icon: Users, color: 'bg-nlp-cream text-nlp-forest' },
            { label: 'New', value: counts.new, icon: Clock, color: 'bg-orange-10 text-orange-70' },
            { label: 'Reviewed', value: counts.reviewed, icon: CheckCircle, color: 'bg-nlp-olive/15 text-nlp-olive' },
            { label: 'Featured', value: counts.featured, icon: Star, color: 'bg-nlp-gold/15 text-nlp-gold-dark' },
          ].map((stat) => (
            <div key={stat.label} className="bg-white rounded-2xl shadow-soft p-5 border border-nlp-cream-dark">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${stat.color}`}>
                <stat.icon className="w-5 h-5" />
              </div>
              <div className="text-2xl font-bold text-nlp-forest">{stat.value}</div>
              <div className="text-xs text-nlp-taupe">{stat.label}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-col lg:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-nlp-taupe" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by name or testimonial..."
              className="w-full pl-10 pr-4 py-3 border border-nlp-cream-dark rounded-xl text-sm bg-nlp-cream/60 text-nlp-forest placeholder:text-nlp-taupe/60 focus:outline-none focus:ring-2 focus:ring-nlp-olive/40 focus:border-nlp-olive"
            />
          </div>
          <select
            value={trainingFilter}
            onChange={(event) => setTrainingFilter(event.target.value)}
            className="px-4 py-3 border border-nlp-cream-dark rounded-xl text-sm bg-white text-nlp-forest focus:outline-none focus:ring-2 focus:ring-nlp-olive/40 focus:border-nlp-olive"
          >
            <option value="all">All training programs</option>
            <option value="7-day-new-model-nlp">7-Day New Model of NLP</option>
            <option value="nlp-os">NLP OS</option>
            <option value="classical-nlp-practitioner">Classical NLP Practitioner</option>
            <option value="new-model-enrolment-method">Enrolment Method Training</option>
          </select>
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="px-4 py-3 border border-nlp-cream-dark rounded-xl text-sm bg-white text-nlp-forest focus:outline-none focus:ring-2 focus:ring-nlp-olive/40 focus:border-nlp-olive"
          >
            <option value="all">All statuses</option>
            <option value="new">New</option>
            <option value="reviewed">Reviewed</option>
            <option value="featured">Featured</option>
            <option value="archived">Archived</option>
          </select>
        </div>

        <div className="grid lg:grid-cols-[1fr_420px] gap-6 items-start">
          <div className="bg-white rounded-2xl shadow-soft overflow-hidden border border-nlp-cream-dark">
            {filteredTestimonials.length === 0 ? (
              <div className="p-16 text-center text-nlp-taupe text-sm">{search || statusFilter !== 'all' || trainingFilter !== 'all' ? 'No testimonials match your filters.' : 'No testimonials have been submitted yet.'}</div>
            ) : (
              <div className="divide-y divide-nlp-cream-dark">
                {filteredTestimonials.map((t, index) => (
                  <motion.button
                    key={t.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.02 }}
                    onClick={() => setSelectedTestimonial(t)}
                    className={`w-full text-left p-5 hover:bg-nlp-cream/30 transition-colors ${selectedTestimonial?.id === t.id ? 'bg-nlp-olive/5 border-l-4 border-nlp-olive' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div>
                        <div className="font-semibold text-nlp-forest">{t.participant_name}</div>
                      </div>
                      <span className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold ${t.review_status === 'new' ? 'bg-orange-10 text-orange-70' : t.review_status === 'featured' ? 'bg-nlp-gold/15 text-nlp-gold-dark' : t.review_status === 'archived' ? 'bg-nlp-cream text-nlp-taupe' : 'bg-nlp-olive/15 text-nlp-olive'}`}>
                        {STATUS_LABELS[t.review_status]}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-nlp-taupe mb-2">
                      <span>{TRAINING_LABELS[t.training_type] || t.training_type}</span>
                      <span>•</span>
                      <span>{new Date(t.submitted_at).toLocaleDateString()}</span>
                      {t.consent_given && (
                        <>
                          <span>•</span>
                          <span className="text-nlp-olive font-medium">Consent given</span>
                        </>
                      )}
                    </div>
                    <p className="text-sm text-nlp-forest/70 line-clamp-2">{t.testimonial_text}</p>
                  </motion.button>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl shadow-soft p-6 border border-nlp-cream-dark lg:sticky lg:top-6">
            {!selectedTestimonial ? (
              <div className="py-16 text-center text-nlp-taupe">
                <Sparkles className="w-10 h-10 mx-auto mb-3 text-nlp-olive/40" />
                <p className="text-sm">Select a testimonial to read and review it.</p>
              </div>
            ) : (
              <div>
                <div className="flex items-start justify-between gap-3 mb-5">
                  <div>
                    <h2 className="text-xl font-bold text-nlp-forest">{selectedTestimonial.participant_name}</h2>
                    {selectedTestimonial.email && <p className="text-sm text-nlp-taupe mt-1">{selectedTestimonial.email}</p>}
                  </div>
                  <span className="text-xs text-nlp-taupe whitespace-nowrap">{TRAINING_LABELS[selectedTestimonial.training_type] || selectedTestimonial.training_type}</span>
                </div>

                {/* Consent badge */}
                <div className={`mb-5 px-4 py-3 rounded-xl border flex items-center gap-2 ${selectedTestimonial.consent_given ? 'bg-nlp-olive/5 border-nlp-olive/20 text-nlp-olive' : 'bg-orange-10/50 border-orange-20 text-orange-70'}`}>
                  {selectedTestimonial.consent_given ? (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      <span className="text-sm font-medium">Consent given — may use on website</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-5 h-5" />
                      <span className="text-sm font-medium">No consent — do not use publicly</span>
                    </>
                  )}
                </div>

                <div className="px-4 py-4 rounded-xl bg-nlp-cream/40 border border-nlp-cream-dark mb-5">
                  <div className="flex items-center gap-2 mb-2 text-nlp-gold-dark text-xs font-bold uppercase tracking-wide">
                    <Star className="w-4 h-4" /> Their testimonial
                  </div>
                  <p className="text-nlp-forest/80 leading-relaxed whitespace-pre-wrap">{selectedTestimonial.testimonial_text}</p>
                </div>

                <div className="border-t border-nlp-cream-dark pt-5">
                  <label className="block text-sm font-semibold text-nlp-forest mb-2">Review status</label>
                  <select
                    value={selectedTestimonial.review_status}
                    onChange={(event) => updateTestimonial({ review_status: event.target.value as TrainingTestimonial['review_status'] })}
                    disabled={isSaving}
                    className="w-full px-3 py-2.5 border border-nlp-cream-dark rounded-xl text-sm bg-white text-nlp-forest mb-4 focus:outline-none focus:ring-2 focus:ring-nlp-olive/40 focus:border-nlp-olive"
                  >
                    <option value="new">New</option>
                    <option value="reviewed">Reviewed</option>
                    <option value="featured">Featured</option>
                    <option value="archived">Archived</option>
                  </select>
                  <label className="block text-sm font-semibold text-nlp-forest mb-2">Private admin note</label>
                  <textarea
                    defaultValue={selectedTestimonial.admin_note || ''}
                    key={selectedTestimonial.id + selectedTestimonial.admin_note}
                    onBlur={(event) => {
                      const note = event.target.value.trim() || null;
                      if (note !== selectedTestimonial.admin_note) updateTestimonial({ admin_note: note });
                    }}
                    disabled={isSaving}
                    rows={4}
                    placeholder="Add a private note about this testimonial..."
                    className="w-full px-3 py-2.5 border border-nlp-cream-dark rounded-xl text-sm bg-white text-nlp-forest placeholder:text-nlp-taupe/60 resize-none focus:outline-none focus:ring-2 focus:ring-nlp-olive/40 focus:border-nlp-olive"
                  />
                  {isSaving && <p className="text-xs text-nlp-taupe mt-2">Saving review...</p>}
                </div>
                <div className="mt-5 pt-4 border-t border-nlp-cream-dark text-xs text-nlp-taupe">
                  Submitted {new Date(selectedTestimonial.submitted_at).toLocaleString()}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
